import { useTypeformContext } from '../../Service/Typeform/TypeformContextProvider';
import { useAcuityContext } from '../../Service/Acuity/AcuityContextProvider';
import { usePaymentContext } from '../../Service/Stripe/PaymentContextProvider';
import { useBlueprintSchedulingContext } from '../../Service/BlueprintScheduling/BlueprintSchedulingContextProvider';
import { BlueprintFlowData, FlowScenario } from '../../types/flowScenario';
import { addNDaysToDate, isPastConversationDate } from '../../utils/shared';
import { StepConfig } from './ProgressViewer';
import React from 'react';
import { normalizeFeedbackDetails } from '../../utils/normalizeFeedbackDetails';

export function useProgressStepsData(
  blueprintFlowData: BlueprintFlowData | undefined,
  flowScenario: FlowScenario
): {
  steps: StepConfig[];
  currentStepIndex: number;
} {
  const { showCheckoutForm, setShowCheckoutForm } = usePaymentContext();
  const { showAcuityDialog, setShowAcuityDialog } = useAcuityContext();
  const { showSchedulingDialog, setShowSchedulingDialog } = useBlueprintSchedulingContext();
  const { showTypeform, setShowTypeform } = useTypeformContext();

  // TODO: Add feature flag to toggle between Acuity and Blueprint scheduling
  const useBlueprintScheduling = true; // For now, always use new scheduling

  // Normalize feedbackDetails for backward compatibility
  const {
    currentFeedback,
    hasMultipleFollowUps,
    allConversationsCompleted,
    shouldShowFeedbackForm,
  } = React.useMemo(
    () => normalizeFeedbackDetails(blueprintFlowData?.feedbackDetails),
    [blueprintFlowData?.feedbackDetails]
  );

  const isOfflinePaymentDone = blueprintFlowData?.isOfflinePaymentDone === true;

  const steps = React.useMemo((): StepConfig[] => {
    // Derive displayDateTime from bookingDetails if available, fallback to blueprintFlowData
    let displayDateTime = blueprintFlowData?.displayDateTime;
    const bookingDetails = blueprintFlowData?.bookingDetails;

    // If bookingDetails exists, derive displayDateTime from scheduledTime in the booking's timezone
    if (bookingDetails?.scheduledTime?.start && bookingDetails?.scheduledTime?.timezone) {
      const startDate = new Date(bookingDetails.scheduledTime.start);

      // Get timezone abbreviation using locale-specific formatting
      const timeString = startDate.toLocaleString('en-US', {
        timeZone: bookingDetails.scheduledTime.timezone,
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      });

      // Extract timezone abbreviation (everything after the last space)
      const tzMatch = timeString.match(/\s([A-Z]{2,5})$/);
      const timezoneName = tzMatch ? tzMatch[1] : 'IST';

      const formatter = new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: bookingDetails.scheduledTime.timezone,
      });

      displayDateTime = `${formatter.format(startDate)} ${timezoneName}`;
    }

    return [
      {
        title: 'Welcome',
        description: 'We are glad you are here',
      },
      ...(!isOfflinePaymentDone
        ? [
            {
              title: 'Payment',
              description: 'Start your wellbeing journey',
              actionButton: {
                label: 'Continue to Pay',
                isDisabled: showCheckoutForm,
                isInProgress: showCheckoutForm,
                onClick: () => {
                  // Handle payment button click
                  setShowCheckoutForm();
                },
              },
            },
          ]
        : []),
      {
        title: 'Schedule',
        description: 'Schedule your conversation with your guide',
        actionButton: useBlueprintScheduling
          ? {
              label: 'Choose a time →',
              isDisabled: showSchedulingDialog,
              isInProgress: showSchedulingDialog,
              onClick: () => {
                setShowSchedulingDialog();
              },
            }
          : {
              label: 'Schedule a slot',
              isDisabled: showAcuityDialog,
              isInProgress: showAcuityDialog,
              onClick: () => {
                // Handle schedule button click
                setShowAcuityDialog();
              },
            },
      },
      {
        title: 'Assessment',
        description: 'Complete your wellbeing assessment',
        actionButton: {
          label: 'Complete assessment',
          isDisabled: showTypeform,
          isInProgress: showTypeform,
          onClick: () => {
            setShowTypeform();
          },
        },
      },
      {
        title: 'Conversation',
        description: `Scheduled for ${displayDateTime}`,
      },
      {
        title: 'Wellbeing Blueprint',
        description: `${blueprintFlowData?.currentState === 6 ? 'Ready to go through' : `${blueprintFlowData?.dateTime ? `Expected by ${addNDaysToDate(blueprintFlowData?.dateTime, 8)}` : ``}`}`,
      },
      {
        title: 'Discussion',
        description: !allConversationsCompleted
          ? !currentFeedback?.conversationScheduled
            ? 'Discuss your blueprint with your guide'
            : `Scheduled for ${currentFeedback?.displayDateTime}`
          : 'All follow-up conversations completed',
        actionButton:
          !allConversationsCompleted &&
          currentFeedback?.currentState === 'activated' &&
          !currentFeedback?.conversationScheduled
            ? currentFeedback?.bookingType
              ? {
                  // Use new blueprint scheduling if bookingType exists
                  label: 'Schedule a slot',
                  isDisabled: showSchedulingDialog,
                  isInProgress: showSchedulingDialog,
                  onClick: () => {
                    setShowSchedulingDialog(true); // Pass true for feedback flow
                  },
                }
              : {
                  // Fallback to Acuity for legacy flows
                  label: 'Schedule a slot',
                  isDisabled: showAcuityDialog,
                  isInProgress: showAcuityDialog,
                  onClick: () => {
                    setShowAcuityDialog();
                  },
                }
            : undefined,
      },
      {
        title: 'Reflection',
        description: 'Share your reflection with us',
        actionButton: shouldShowFeedbackForm
          ? {
              label: 'Begin reflection',
              isDisabled: showTypeform,
              isInProgress: showTypeform,
              onClick: () => {
                setShowTypeform();
              },
            }
          : undefined,
      },
    ];
  }, [
    allConversationsCompleted,
    blueprintFlowData?.bookingDetails,
    blueprintFlowData?.currentState,
    blueprintFlowData?.dateTime,
    blueprintFlowData?.displayDateTime,
    currentFeedback?.bookingType,
    currentFeedback?.conversationScheduled,
    currentFeedback?.currentState,
    currentFeedback?.displayDateTime,
    setShowAcuityDialog,
    setShowCheckoutForm,
    setShowSchedulingDialog,
    setShowTypeform,
    shouldShowFeedbackForm,
    showAcuityDialog,
    showCheckoutForm,
    showSchedulingDialog,
    showTypeform,
    useBlueprintScheduling,
    isOfflinePaymentDone,
  ]);

  const currentStateIndex = React.useMemo(() => {
    if (!blueprintFlowData) {
      return 0;
    }

    const currentState = blueprintFlowData.currentState;
    if (!currentState) {
      return 0;
    }

    let rawIndex: number;

    if (currentState <= 4) {
      rawIndex = currentState;
    } else if (currentState === 5) {
      if (
        blueprintFlowData.dateTime &&
        isPastConversationDate(blueprintFlowData.dateTime) &&
        blueprintFlowData.conversationCompleted
      ) {
        rawIndex = 6;
      } else {
        rawIndex = 5;
      }
    } else if (currentState === 6) {
      if (blueprintFlowData.enableFeedback && !allConversationsCompleted) {
        rawIndex = 7;
      } else if (blueprintFlowData.enableFeedback && shouldShowFeedbackForm) {
        rawIndex = 8;
      } else if (
        blueprintFlowData.enableFeedback &&
        allConversationsCompleted &&
        currentFeedback?.formSubmitted
      ) {
        rawIndex = 9;
      } else {
        rawIndex = 6;
      }
    } else {
      rawIndex = 7;
    }

    // When offline payment done, Payment step is removed (was at position 2).
    // Subtract 1 from any index > 1 to compensate for the missing step.
    return isOfflinePaymentDone && rawIndex > 1 ? rawIndex - 1 : rawIndex;
  }, [blueprintFlowData, currentFeedback, allConversationsCompleted, shouldShowFeedbackForm, isOfflinePaymentDone]);

  if (!blueprintFlowData) {
    return {
      steps: [],
      currentStepIndex: 0,
    };
  }

  const flowScenarioInResponse = blueprintFlowData.flowScenario;

  if (flowScenario !== flowScenarioInResponse) {
    throw new Error('Flow scenario mismatch');
  }

  return {
    steps,
    currentStepIndex: currentStateIndex,
  };
}
